import { z } from "zod";

/** Line 3a of Form W-9 (Rev. March 2024), in the form's order. */
export const W9_CLASSIFICATIONS = ["individual", "c_corp", "s_corp", "partnership", "trust", "llc", "other"] as const;
export type W9Classification = (typeof W9_CLASSIFICATIONS)[number];
export const W9_CLASSIFICATION_LABELS: Record<W9Classification, string> = {
  individual: "Individual / sole proprietor",
  c_corp: "C corporation",
  s_corp: "S corporation",
  partnership: "Partnership",
  trust: "Trust / estate",
  llc: "LLC",
  other: "Other",
};

/**
 * The certification on the form (Part II), word for word: what they agree to, under penalties of
 * perjury, by signing it in the app.
 */
export const W9_CERTIFICATION = `Under penalties of perjury, I certify that:
1. The number shown on this form is my correct taxpayer identification number (or I am waiting for a number to be issued to me); and
2. I am not subject to backup withholding because (a) I am exempt from backup withholding, or (b) I have not been notified by the Internal Revenue Service (IRS) that I am subject to backup withholding as a result of a failure to report all interest or dividends, or (c) the IRS has notified me that I am no longer subject to backup withholding; and
3. I am a U.S. citizen or other U.S. person (defined in the form's instructions); and
4. The FATCA code(s) entered on this form (if any) indicating that I am exempt from FATCA reporting is correct.`;

const text = (max: number) => z.string().trim().max(max).default("");

/** A W-9 filled in and signed in the app. The taxpayer ID is digits only (9). */
export const w9InputSchema = z
  .object({
    name: z.string().trim().min(1, "Enter your name as shown on your income tax return").max(120),
    businessName: text(120),
    classification: z.enum(W9_CLASSIFICATIONS).default("individual"),
    /** LLC: how it's taxed (C, S, or P). */
    llcClass: z.enum(["C", "S", "P", ""]).default(""),
    otherText: text(80),
    /** Line 3b: a partnership, trust, or estate with foreign partners, owners, or beneficiaries. */
    foreignPartners: z.boolean().default(false),
    exemptPayeeCode: text(4),
    fatcaCode: text(4),
    /** Lines 5 and 6; saved to their staff profile when they sign (the newest address wins). */
    address1: z.string().trim().min(1, "Enter your street address").max(120),
    address2: text(60),
    city: z.string().trim().min(1, "Enter your city").max(60),
    state: z.string().trim().min(2, "Enter your state").max(30),
    postalCode: z.string().trim().min(3, "Enter your ZIP").max(12),
    tinType: z.enum(["ssn", "ein"]).default("ssn"),
    tin: z
      .string()
      .transform((v) => v.replace(/\D/g, ""))
      .pipe(z.string().regex(/^\d{9}$/, "Enter all 9 digits")),
    /** Cross out item 2 (the IRS told them they're subject to backup withholding). */
    crossOutItem2: z.boolean().default(false),
    /** They agreed to the certification. */
    agree: z.literal(true, { message: "Agree to the certification to sign" }),
  })
  .superRefine((v, ctx) => {
    if (v.classification === "llc" && !v.llcClass) ctx.addIssue({ code: "custom", path: ["llcClass"], message: "Pick how the LLC is taxed" });
    if (v.classification === "other" && !v.otherText) ctx.addIssue({ code: "custom", path: ["otherText"], message: "Say what kind" });
    if (v.tinType === "ssn" && /^(000|666|9)/.test(v.tin)) ctx.addIssue({ code: "custom", path: ["tin"], message: "That isn't a valid Social Security number" });
  });
export type W9Input = z.input<typeof w9InputSchema>;

/** What the app shows about someone's W-9 (never the full taxpayer ID). */
export interface TaxFormView {
  id: string;
  kind: "signed" | "uploaded";
  name: string;
  businessName: string;
  classification: W9Classification | null;
  tinType: "ssn" | "ein" | null;
  /** "•••-••-1234" or "••-•••1234". */
  tinMasked: string | null;
  signedAt: string;
  createdByName: string | null;
  replacedAt: string | null;
}

export interface StaffTaxForms {
  requestedAt: string | null;
  current: TaxFormView | null;
  history: TaxFormView[];
}

export const maskTin = (type: "ssn" | "ein" | null, last4: string | null) => (!last4 ? null : type === "ein" ? `••-•••${last4}` : `•••-••-${last4}`);
